import { Box, Skeleton, Typography, styled } from "@mui/material";
import CabinetPage from "../../components/Common/CabinetPage";
import { Calendar, Leader } from "../../store";
import { CabButton } from "@CabComponents/CabButton";
import colors from "../../colors";
import { ExecutiveCalendars } from "../../components/ManageCalendars/ExecutiveCalendars";
import { isMobile } from "../../utils/screenSizeUtils";
import { CabIcon } from "@CabComponents/CabIcon";
import { IoCalendarOutline } from 'react-icons/io5';

interface CalendarsProps {
  calendars: Calendar[];
  leaders: Leader[];
  openAddEditModal: (leaderId: number, calendarId?: number) => void;
  removeCalendar: (leaderId: number, calendarId: number) => void;
  loading: boolean;
  leadersLoaded: boolean;
  hasCrmFeature?: boolean;
  embedded?: boolean;
}

const Calendars = ({
  calendars, leaders, openAddEditModal, removeCalendar, loading, leadersLoaded, hasCrmFeature, embedded = false
}: CalendarsProps) => {

  return (
    <Box
      sx={{
        padding: embedded ? 0 : 4,
        display: 'flex',
        flexDirection: 'column',
        gap: 2,
        width: '100%',
        minWidth: 0,
        boxSizing: "border-box"
      }}>
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          gap: .5,
          maxWidth: 700
        }}>
        <Typography variant='body1' color={colors.black700}>
          Choose the calendars you'd like to have visible for each executive when scheduling meetings
        </Typography>
      </Box>
      <Box
        sx={{
          display: "flex",
          gap: 3,
          justifyItems: 'flex-start',
          flexDirection: { xs: 'column', lg: 'row' },
          minWidth: 0
        }}>
        <Box
          sx={{
            display: "flex",
            flex: "1 1 0",
            minWidth: 0,
            flexDirection: 'column',
            gap: 2,
            maxWidth: 1000
          }}>
          {leadersLoaded ? (
            leaders.filter(l => l.permissions.scheduling.edit || l.permissions.scheduling.add_to_meeting)
              .map(leader => (
                <Box key={leader.id}> 
                  <ExecutiveCalendars
                    leader={leader}
                    leaderCalendars={calendars.filter(c => c.leaders.includes(leader.id))}
                    openAddEditModal={openAddEditModal}
                    removeCalendar={removeCalendar}
                    loading={loading}
                    hasCrmFeature={hasCrmFeature}
                  />
                </Box>
              ))
          ) : (
            <SkeletonLeaderCalendar />
          )}
        </Box>
      </Box>
    </Box>
  );
};
interface ManageCalendarsProps {
  calendars: Calendar[];
  leaders: Leader[];
  onManageAccountsClick: () => void;
  openAddEditModal: (leaderId: number, calendarId?: number) => void;
  removeCalendar: (leaderId: number, calendarId: number) => void;
  hasGrant: boolean;
  loading: boolean;
  leadersLoaded: boolean;
  hasCrmFeature?: boolean;
  embedded?: boolean;
}

const ManageCalendars = ({
  calendars, onManageAccountsClick, leaders, openAddEditModal, removeCalendar, hasGrant, loading, leadersLoaded,
  hasCrmFeature, embedded = false
}: ManageCalendarsProps) => {

  const content = (
    <>
      {hasGrant ? (
        <Calendars
          calendars={calendars}
          leaders={leaders}
          openAddEditModal={openAddEditModal}
          removeCalendar={removeCalendar}
          loading={loading}
          leadersLoaded={leadersLoaded}
          hasCrmFeature={hasCrmFeature}
          embedded={embedded}
        />
      ) : (
        <MessageContainer>
          <CabIcon Icon={IoCalendarOutline} sx={{ fontSize: 60 }} />
          <Typography variant="body1" sx={{margin: "24px 0"}}>
            You're not currently signed into any calendar provider.
            Sign in so we can help you schedule meetings faster!
          </Typography>
          <CabButton buttonType="tertiary" color="primary" onClick={onManageAccountsClick}>
            Connect Your Calendars
          </CabButton>
        </MessageContainer>
      )}
    </>
  );

  if (embedded) {
    return content;
  }

  return (
    <CabinetPage
      pageName={'Manage Calendars'}
      headerContent={
        <Box
          sx={{
            display: "flex",
            flexDirection: "row",
            justifyContent: "space-between",
            width: "100%"
          }}>
          <Typography variant="h1">
            Calendar Setup
          </Typography>
          {!isMobile() && (
            <CabButton
              onClick={onManageAccountsClick}
            >
              Manage Integrations
            </CabButton>
          )}
        </Box>
      }
    >
      {content}
    </CabinetPage>
  );
};

export default ManageCalendars;

const MessageContainer = styled("span", {label: "MessageContainer"})({
  display: "flex",
  flexDirection: "column",
  justifyContent: "center",
  alignItems: "center",
  padding: "42px",
});

const StyledBox = styled(Box, {label: "StyledBox"})(() => ({
  boxSizing: 'border-box',
  width: '100%',
  maxWidth: 900,
  minWidth: 0,
  border: '1px solid',
  borderColor: colors.black200,
  borderRadius: 6,
  padding: 16,
  marginRight: 8
}));

const SkeletonLeaderCalendar = () => {
  return (
    <StyledBox>
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'space-between',
          marginBottom: 1
        }}>
        <Box
          sx={{
            display: 'flex',
            gap: 1
          }}>
          <Skeleton variant='circular' width={28} height={28} />
          <Skeleton width={200} height={30} />
        </Box>
        <Skeleton width={100} height={30} />
      </Box>
      <Box
        sx={{
          paddingTop: 1.5,
          paddingBottom: 1.5,
          justifyContent: 'space-between',
          display: 'flex',
          alignItems: 'center',
          gap: 1
        }}>
        <Skeleton height={50} width={'100%'} />
      </Box>
    </StyledBox>
  );
};
